/**
 * LiveAudio (web) — le son de la salle, dans un navigateur, partagé par le live de l'élève et celui du
 * tuteur (même composant, même rendu) :
 *  - le rendu audio (`<RoomAudioRenderer>`) n'est monté qu'en Dialogue : en Concentré, aucune
 *    balise `<audio>`, le silence est garanti par la structure (`docs/sound-mode-architecture.md`),
 *    comme `LKVideoCallPortal.web.js` ; le tuteur le monte toujours en Dialogue (`dialogue`), comme
 *    l'ancienne barre (`ControlBarM.web.tsx`, `isSoundMode || isTutor` de `LiveModeFooter.js`, volume 1) ;
 *  - quand le navigateur a mis la lecture en pause (lecture automatique bloquée après une coupure ou
 *    un retour d'onglet, `canPlaybackAudio`), une boîte demande un toucher, qui relance le son
 *    (`room.startAudio()`, dans le geste) — l'ancien `StartAudioOverlay`. Elle s'efface derrière la
 *    boîte du micro (`covered`), dont le toucher relance aussi le son : une boîte, un toucher.
 */
import { RoomAudioRenderer, useRoomContext } from "@livekit/components-react"
import { RoomEvent } from "livekit-client"
import React, { useEffect, useState } from "react"

import { ConfirmDialog } from "../../../../../../design-system"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"

const TEXTS = {
    title: "Touche pour réactiver le son",
    message: "Ton navigateur a mis le son du live en pause. Un simple appui le relance.",
    confirm: "Réactiver le son",
}

export function LiveAudio({
    dialogue,
    covered = false,
    telemetry,
}: {
    dialogue: boolean
    /** La boîte du micro est à l'écran : son toucher relance le son, celle-ci attend derrière. */
    covered?: boolean
    telemetry: EngineTelemetry
}) {
    const room = useRoomContext()
    const [blocked, setBlocked] = useState(false)

    useEffect(() => {
        if (room == null) return undefined
        const sync = () => setBlocked(!room.canPlaybackAudio)
        sync()
        room.on(RoomEvent.AudioPlaybackStatusChanged, sync)
        return () => {
            room.off(RoomEvent.AudioPlaybackStatusChanged, sync)
        }
    }, [room])

    const resume = () => {
        room.startAudio()
            .catch((cause: unknown) => {
                telemetry.error("start_audio", cause)
            })
            .finally(() => setBlocked(!room.canPlaybackAudio))
    }

    return (
        <>
            {dialogue && <RoomAudioRenderer volume={1} />}
            <ConfirmDialog
                visible={blocked && !covered}
                title={TEXTS.title}
                message={TEXTS.message}
                confirmLabel={TEXTS.confirm}
                danger={false}
                hideCancel
                onConfirm={resume}
                onCancel={resume}
            />
        </>
    )
}
